import React from 'react';
import {
  Form, Button, Row, Col, Table, Card,
} from 'react-bootstrap';

const defaultBand = (currency) => ({
  up_to_km: 0,
  ...(currency === 'EUR'
    ? { flat_rate_eur: '', per_km_eur: '' }
    : { flat_rate_huf: '', per_km_huf: '' }),
});

const defaultHourly = (currency) => (currency === 'EUR'
  ? { hourly_rate_eur: '', minimum_hours: 1, waiting_time: { per_hour_eur: '' } }
  : { hourly_rate_huf: '', minimum_hours: 1, waiting_time: { per_hour_huf: '' } });

function OperatorVehicleBandsEditor({
  vehicleBands = [],
  vehicleCategories = [],
  currency = 'EUR',
  onChange,
}) {
  const usedCategoryIds = new Set(vehicleBands.map((vb) => vb.vehicle_category_id));
  const availableCategories = vehicleCategories.filter(
    (cat) => cat.active === 1 && !usedCategoryIds.has(cat.Id),
  );

  const addEntry = (categoryId) => {
    onChange([
      ...vehicleBands,
      {
        vehicle_category_id: categoryId,
        bands: [defaultBand(currency)],
      },
    ]);
  };

  const removeEntry = (index) => {
    const next = [...vehicleBands];
    next.splice(index, 1);
    onChange(next);
  };

  const updateEntry = (index, patch) => {
    const next = [...vehicleBands];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const handleBandChange = (entryIdx, bandIdx, field, value) => {
    const entry = vehicleBands[entryIdx];
    const bands = [...(entry.bands || [])];
    bands[bandIdx] = { ...bands[bandIdx], [field]: value };
    updateEntry(entryIdx, { bands });
  };

  const addBand = (entryIdx) => {
    const entry = vehicleBands[entryIdx];
    const bands = [...(entry.bands || [])];
    // Insert before unlimited band (up_to_km === 0) if any
    const unlimitedIdx = bands.findIndex((b) => Number(b.up_to_km) === 0);
    if (unlimitedIdx >= 0) {
      bands.splice(unlimitedIdx, 0, { ...defaultBand(currency), up_to_km: 100 });
    } else {
      bands.push(defaultBand(currency));
    }
    updateEntry(entryIdx, { bands });
  };

  const removeBand = (entryIdx, bandIdx) => {
    const entry = vehicleBands[entryIdx];
    const bands = [...(entry.bands || [])];
    bands.splice(bandIdx, 1);
    updateEntry(entryIdx, { bands });
  };

  const toggleHourly = (entryIdx, enabled) => {
    if (enabled) {
      updateEntry(entryIdx, { hourly: defaultHourly(currency) });
    } else {
      const entry = { ...vehicleBands[entryIdx] };
      delete entry.hourly;
      const next = [...vehicleBands];
      next[entryIdx] = entry;
      onChange(next);
    }
  };

  const handleHourlyChange = (entryIdx, field, value) => {
    const entry = vehicleBands[entryIdx];
    const hourly = { ...(entry.hourly || {}), [field]: value };
    updateEntry(entryIdx, { hourly });
  };

  const handleWaitingTimeChange = (entryIdx, field, value) => {
    const entry = vehicleBands[entryIdx];
    const hourly = {
      ...(entry.hourly || {}),
      waiting_time: { ...(entry.hourly?.waiting_time || {}), [field]: value },
    };
    updateEntry(entryIdx, { hourly });
  };

  const getCategoryName = (id) => {
    const cat = vehicleCategories.find((c) => c.Id === id);
    return cat ? cat.name : id;
  };

  const flatRateField = currency === 'EUR' ? 'flat_rate_eur' : 'flat_rate_huf';
  const perKmField = currency === 'EUR' ? 'per_km_eur' : 'per_km_huf';
  const hourlyRateField = currency === 'EUR' ? 'hourly_rate_eur' : 'hourly_rate_huf';
  const waitingPerHourField = currency === 'EUR' ? 'per_hour_eur' : 'per_hour_huf';

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Vehicle Bands</h5>
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            style={{ width: '240px' }}
            value=""
            onChange={(e) => {
              if (e.target.value) addEntry(e.target.value);
            }}
            disabled={availableCategories.length === 0}
          >
            <option value="">
              {availableCategories.length === 0
                ? 'All categories configured'
                : '+ Add vehicle category'}
            </option>
            {availableCategories.map((cat) => (
              <option key={cat.Id} value={cat.Id}>
                {cat.name}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {vehicleBands.length === 0 && (
        <div className="alert alert-warning small">
          No vehicle bands configured. Quotes that need distance-based pricing will fail until you
          add at least one vehicle category band entry.
        </div>
      )}

      {vehicleBands.map((entry, entryIdx) => {
        const bands = entry.bands || [];
        return (
          <Card key={entry.vehicle_category_id} className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>{getCategoryName(entry.vehicle_category_id)}</strong>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeEntry(entryIdx)}
              >
                <i className="fa fa-trash me-1" />
                Remove
              </Button>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Distance bands</h6>
                <Button variant="outline-primary" size="sm" onClick={() => addBand(entryIdx)}>
                  <i className="fa fa-plus me-1" />
                  Add band
                </Button>
              </div>
              <Table responsive bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '120px' }}>Up to KM</th>
                    <th>{`Flat rate (${currency})`}</th>
                    <th>{`Per KM (${currency})`}</th>
                    <th style={{ width: '60px' }} />
                  </tr>
                </thead>
                <tbody>
                  {bands.map((band, bandIdx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`band-${bandIdx}`}>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={0}
                          value={band.up_to_km ?? ''}
                          onChange={(e) => handleBandChange(entryIdx, bandIdx, 'up_to_km', e.target.value)}
                        />
                        {Number(band.up_to_km) === 0 && (
                          <small className="text-muted">Unlimited (final band)</small>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          step="0.01"
                          value={band[flatRateField] ?? ''}
                          onChange={(e) => (
                            handleBandChange(entryIdx, bandIdx, flatRateField, e.target.value)
                          )}
                          placeholder="—"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          step="0.01"
                          value={band[perKmField] ?? ''}
                          onChange={(e) => (
                            handleBandChange(entryIdx, bandIdx, perKmField, e.target.value)
                          )}
                          placeholder="—"
                        />
                      </td>
                      <td className="text-center">
                        {bands.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeBand(entryIdx, bandIdx)}
                            title="Remove band"
                          >
                            <i className="fa fa-trash" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Form.Check
                type="switch"
                id={`hourly-toggle-${entry.vehicle_category_id}`}
                label="Enable hourly rental pricing"
                checked={!!entry.hourly}
                onChange={(e) => toggleHourly(entryIdx, e.target.checked)}
                className="mb-2"
              />
              {entry.hourly && (
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">{`Hourly rate (${currency})`}</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        step="0.01"
                        value={entry.hourly[hourlyRateField] ?? ''}
                        onChange={(e) => (
                          handleHourlyChange(entryIdx, hourlyRateField, e.target.value)
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Minimum hours</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        min={1}
                        value={entry.hourly.minimum_hours ?? 1}
                        onChange={(e) => handleHourlyChange(entryIdx, 'minimum_hours', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">{`Waiting / hr (${currency})`}</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        step="0.01"
                        value={entry.hourly.waiting_time?.[waitingPerHourField] ?? ''}
                        onChange={(e) => (
                          handleWaitingTimeChange(entryIdx, waitingPerHourField, e.target.value)
                        )}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}

export default OperatorVehicleBandsEditor;
